import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { featuresData } from '@/mocks/homepageData';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

export default function FeaturesSection() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  const getSpanClass = (span: string) => {
    const spanMap: Record<string, string> = {
      'col-span-1 md:col-span-2 row-span-1': 'md:col-span-2',
      'col-span-1 row-span-1': '',
    };
    return spanMap[span] || '';
  };

  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#999]' : 'text-[#666]';

  return (
    <section
      id="features"
      ref={sectionRef}
      className={`relative w-full py-16 md:py-24 border-t ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'}`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 md:mb-14"
        >
          <span className="text-xs text-[#00ff88] font-mono uppercase tracking-widest">{t('features.productSuite')}</span>
          <h2 className={`mt-3 text-2xl md:text-3xl lg:text-4xl font-bold ${textPrimary}`}>
            {t('features.everythingNeed')}
          </h2>
          <p className={`mt-3 text-sm md:text-base max-w-2xl mx-auto ${textSecondary}`}>
            {t('features.description')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {featuresData.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`glass-card-hover p-5 md:p-6 group relative overflow-hidden ${getSpanClass(feature.span)}`}
            >
              {/* Background glow on hover */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#00ff88]/5 rounded-full blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10">
                <div className="w-10 h-10 rounded-lg bg-[#00ff88]/10 border border-[#00ff88]/20 flex items-center justify-center mb-4 group-hover:bg-[#00ff88]/20 group-hover:border-[#00ff88]/40 transition-all">
                  <i className={`${feature.icon} w-5 h-5 flex items-center justify-center text-[#00ff88]`} />
                </div>

                <h3 className={`text-base md:text-lg font-semibold mb-2 group-hover:text-[#00ff88] transition-colors ${textPrimary}`}>
                  {t(`content.features.${feature.id}.title`, feature.title)}
                </h3>
                <p className={`text-sm leading-relaxed ${textSecondary}`}>
                  {t(`content.features.${feature.id}.description`, feature.description)}
                </p>

                {/* Arrow link */}
                <div className={`mt-4 flex items-center gap-1 text-xs group-hover:text-[#00ff88] transition-colors ${isDark ? 'text-[#666]' : 'text-[#888]'}`}>
                  <span className="font-medium">{t('features.learnMore')}</span>
                  <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}